import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { cashDrawerAPI } from '../../services/api';
import OpenDrawerDialog from './OpenDrawerDialog';
import { clinicToday } from '../../utils/clinicTime';

/**
 * The signed-in cashier's cash drawer, for every screen that takes or hands back money.
 *
 *   const { withDrawer } = useCashDrawer();
 *   const token = await withDrawer(() => visitAPI.create(data), { cash: mode === 'CASH' });
 *
 * With `cash`, someone whose drawer is not open is asked for the starting cash first, and the
 * action runs once the drawer is open. The server holds the same rule, so when the drawer was closed
 * somewhere else in the meantime, its DRAWER_NOT_OPEN refusal asks again and the action is tried
 * once more. Backing out of the question throws a DrawerCancelledError: nothing happened, and callers
 * say nothing about it.
 *
 * Outside the provider (a screen rendered on its own, or in a test) every action passes straight
 * through.
 */

export class DrawerCancelledError extends Error {
  constructor() {
    super('The cash drawer was not opened.');
    this.name = 'DrawerCancelledError';
  }
}

export const isDrawerCancelled = (err) =>
  err instanceof DrawerCancelledError || err?.name === 'DrawerCancelledError';

export const isDrawerNotOpen = (err) => (err?.data?.code || err?.body?.code) === 'DRAWER_NOT_OPEN';

/**
 * Whether this drawer was opened on an earlier day. It has to be counted and closed before the new
 * day's billing starts (owner, 2026-10-02), so each day's cash is counted on its own. Opening times
 * come from the server in clinic time, so their date part is the clinic's date.
 */
export const openedBeforeToday = (drawer) => {
  const day = drawer?.openedAt ? String(drawer.openedAt).slice(0, 10) : null;
  return Boolean(day && day !== clinicToday());
};

/**
 * Whether handing back a fee paid this way takes cash out of the drawer. A token with no method
 * recorded was paid in cash, which is how the server treats it too.
 */
export const refundsInCash = (paymentMode) =>
  !paymentMode || paymentMode === 'NONE' || paymentMode === 'CASH';

const passThrough = {
  active: false,
  loaded: true,
  drawer: null,
  refresh: async () => null,
  setDrawer: () => {},
  askToOpen: async () => false,
  withDrawer: (action) => action(),
};

const CashDrawerContext = createContext(passThrough);

export const useCashDrawer = () => useContext(CashDrawerContext);

export const CashDrawerProvider = ({ children }) => {
  const [drawer, setDrawerState] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [question, setQuestion] = useState(null); // { reason, resolve } while the open dialog is up
  const drawerRef = useRef(null);

  const setDrawer = useCallback((next) => {
    drawerRef.current = next || null;
    setDrawerState(next || null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await cashDrawerAPI.mine();
      setDrawer(res?.drawer || null);
      return res?.drawer || null;
    } catch {
      return drawerRef.current;
    } finally {
      setLoaded(true);
    }
  }, [setDrawer]);

  useEffect(() => { refresh(); }, [refresh]);

  // The drawer can be opened or closed in another tab: look again when this one comes back into view.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  // Resolves true once the drawer is open, false if the cashier backs out.
  const askToOpen = useCallback((reason = 'manual') => new Promise((resolve) => {
    setQuestion((previous) => {
      if (previous) previous.resolve(false);
      return { reason, resolve };
    });
  }), []);

  const ensureOpen = useCallback(async () => {
    if (drawerRef.current) return true;
    if (await refresh()) return true;
    return askToOpen('cash');
  }, [refresh, askToOpen]);

  const withDrawer = useCallback(async (action, { cash = false } = {}) => {
    if (cash && !(await ensureOpen())) throw new DrawerCancelledError();
    try {
      return await action();
    } catch (err) {
      if (!isDrawerNotOpen(err)) throw err;
      setDrawer(null);
      if (!(await askToOpen('refused'))) throw new DrawerCancelledError();
      return action();
    }
  }, [ensureOpen, askToOpen, setDrawer]);

  const answer = (opened) => {
    if (!question) return;
    if (opened) setDrawer(opened);
    question.resolve(Boolean(opened));
    setQuestion(null);
  };

  const value = useMemo(
    () => ({ active: true, loaded, drawer, refresh, setDrawer, askToOpen, withDrawer }),
    [loaded, drawer, refresh, setDrawer, askToOpen, withDrawer],
  );

  return (
    <CashDrawerContext.Provider value={value}>
      {children}
      {question && (
        <OpenDrawerDialog
          reason={question.reason}
          onOpened={(opened) => answer(opened)}
          onCancel={() => answer(null)}
        />
      )}
    </CashDrawerContext.Provider>
  );
};
